// Shared create/edit project form (docs/05-raster-grading.md §8). The create
// and edit modals render the same body — only the title and submit verb differ.
// Layout top→down: name · language · score format · exam · notes.
//
// Create: POST /api/projects/, then (if an exam file was chosen) POST /set_exam.
// Edit:   PATCH /api/projects/{id}, then (if a new exam file was chosen) /set_exam.

import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch, apiJson } from "../api/client";
import type { Language, Project, ScoreFormat } from "../api/types";

import "./CreateProjectModal.css";

const SCORE_FORMATS: ScoreFormat[] = ["0-15", "1-6", "A-F", "0-100"];

interface Props {
  mode: "create" | "edit";
  project?: Project;
  onClose: () => void;
  onDone: (project: Project) => void;
}

export function ProjectForm({ mode, project, onClose, onDone }: Props) {
  const { t } = useTranslation();
  const headingId = useId();

  const [name, setName] = useState(project?.name ?? "");
  const [language, setLanguage] = useState<Language>(project?.language ?? "en");
  const [scoreFormat, setScoreFormat] = useState<ScoreFormat>(project?.score_format ?? "0-15");
  const [notes, setNotes] = useState(project?.notes ?? "");
  const [examFile, setExamFile] = useState<File | null>(null);
  const examInputRef = useRef<HTMLInputElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasExam = !!project?.exam_pdf_path;

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !submitting) onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    if (name.trim().length === 0) {
      setError(t("project_modal.error_no_name"));
      return;
    }

    setSubmitting(true);
    try {
      const body = JSON.stringify({
        name: name.trim(),
        language,
        score_format: scoreFormat,
        notes: notes.trim() || null,
      });
      const saved =
        mode === "create"
          ? await apiJson<Project>("/api/projects/", { method: "POST", body })
          : await apiJson<Project>(`/api/projects/${project!.id}`, { method: "PATCH", body });

      if (examFile) {
        const fd = new FormData();
        fd.append("file", examFile);
        const res = await apiFetch(`/api/projects/${saved.id}/set_exam`, {
          method: "POST",
          body: fd,
          headers: {},
        });
        if (!res.ok) {
          throw new ApiError(res.status, await safeText(res), "exam upload failed");
        }
      }
      onDone(saved);
    } catch (err) {
      const detail = err instanceof ApiError ? extractDetail(err.body) : null;
      setError(detail ?? (err as Error).message ?? t("project_modal.error_create_failed"));
    } finally {
      setSubmitting(false);
    }
  }

  const title = mode === "create" ? t("project_modal.create_title") : t("project_modal.edit_title");
  const submitLabel =
    mode === "create"
      ? submitting
        ? t("project_modal.creating")
        : t("project_modal.create_btn")
      : submitting
        ? t("common.saving")
        : t("common.save");

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <form
        className="modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        onSubmit={onSubmit}
      >
        <button type="button" className="modal-close" onClick={onClose} disabled={submitting} aria-label="Close">✕</button>

        <h2 id={headingId} className="modal-card__title">{title}</h2>

        <label className="modal-row">
          <span className="modal-row__label">{t("project_modal.name_label")}</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Class 10B — March 2026"
            autoFocus
            required
            disabled={submitting}
          />
        </label>

        <div className="modal-row">
          <span className="modal-row__label">{t("project_modal.language_label")}</span>
          <div className="modal-radio" role="group" aria-label={t("project_modal.language_label")}>
            {(["en", "de"] as Language[]).map((val) => (
              <button
                key={val}
                type="button"
                className={language === val ? "active" : ""}
                onClick={() => setLanguage(val)}
                disabled={submitting}
              >
                {val === "en" ? t("common.english") : t("common.german")}
              </button>
            ))}
          </div>
        </div>

        <div className="modal-row">
          <span className="modal-row__label">{t("project_modal.score_format_label")}</span>
          <div className="modal-radio" role="group" aria-label={t("project_modal.score_format_label")}>
            {SCORE_FORMATS.map((sf) => (
              <button
                key={sf}
                type="button"
                className={scoreFormat === sf ? "active" : ""}
                onClick={() => setScoreFormat(sf)}
                disabled={submitting}
              >
                {sf}
              </button>
            ))}
          </div>
        </div>

        <div className="modal-row">
          <span className="modal-row__label">{t("project_modal.exam_label")}</span>
          <div className="modal-file-row">
            <button
              type="button"
              className="modal-btn modal-btn--accent modal-btn--sm"
              onClick={() => examInputRef.current?.click()}
              disabled={submitting}
            >
              {t("project_modal.choose_file")}
            </button>
            <span className="modal-file-name">
              {examFile ? examFile.name : hasExam ? t("project_modal.exam_uploaded") : t("project_modal.no_file")}
            </span>
            <input
              ref={examInputRef}
              type="file"
              accept="application/pdf,.txt,.md,text/plain,text/markdown"
              onChange={(e) => setExamFile(e.target.files?.[0] ?? null)}
              disabled={submitting}
              style={{ display: "none" }}
            />
          </div>
        </div>

        <label className="modal-row">
          <span className="modal-row__label">{t("project_modal.notes_label")}</span>
          <textarea
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t("project_modal.notes_placeholder")}
            disabled={submitting}
          />
        </label>

        {error && <div className="modal-error" role="alert">{error}</div>}

        <div className="modal-actions">
          <button type="button" className="modal-btn modal-btn--ghost" onClick={onClose} disabled={submitting}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="modal-btn modal-btn--accent" disabled={submitting || name.trim().length === 0}>
            {submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

async function safeText(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

function extractDetail(body: unknown): string | null {
  if (body && typeof body === "object" && "detail" in body) {
    const detail = (body as { detail: unknown }).detail;
    if (typeof detail === "string") return detail;
  }
  return null;
}
